import { Badge } from '@shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/shadcn/tabs';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FulfillmentData,
  OwnBoxPackaging,
  PackagingRateItemData,
  RateItem,
  RateItemType,
  ShippingData,
  StorageData,
} from '../../models/rate.types';
import { RateItemDataFallback } from '../feedback/RateItemDataFallback';
import { ShippingFilters } from '../interaction/ShippingFilters';
import { FulfillmentDataDisplay } from './FulfillmentDataDisplay';
import { PackagingDataDisplay } from './PackagingDataDisplay';
import { ShippingDataDisplay } from './ShippingDataDisplay';
import { StorageDataDisplay } from './StorageDataDisplay';
import { partitionRateItems } from '../../utils/packagingRateItem';
import { extractShippingContext, formatShippingContextSummary, hasShippingContext } from '../../utils/shippingContext';

interface RateItemsTableProps {
  items: RateItem[];
  ownBoxes?: OwnBoxPackaging[];
}

const TypeBadge = ({ type }: { type: RateItemType }) => {
  const { t } = useTranslation('ratesModule');

  const config = useMemo(() => {
    switch (type) {
      case 'shipping':
        return {
          label: t('itemTypes.shipping'),
          className: 'bg-bg-info text-info',
        };
      case 'fulfillment':
        return {
          label: t('itemTypes.fulfillment'),
          className: 'bg-bg-warning text-warning',
        };
      case 'storage':
        return {
          label: t('itemTypes.storage'),
          className: 'bg-bg-success text-success',
        };
      case 'packaging':
        return {
          label: t('itemTypes.packaging'),
          className: 'bg-bg-info text-info',
        };
      default:
        return { label: type, className: 'bg-card-border text-text' };
    }
  }, [type, t]);

  return <Badge className={`text-xs ${config.className}`}>{config.label}</Badge>;
};

const RateItemDataRenderer = ({ item, ownBoxes = [] }: { item: RateItem; ownBoxes?: OwnBoxPackaging[] }) => {
  if (!item.data || typeof item.data !== 'object') {
    return <RateItemDataFallback data={item.data} />;
  }

  switch (item.type) {
    case 'shipping':
      return <ShippingDataDisplay data={item.data as ShippingData} />;
    case 'fulfillment':
      return <FulfillmentDataDisplay data={item.data as FulfillmentData} />;
    case 'storage':
      return <StorageDataDisplay data={item.data as StorageData} />;
    case 'packaging':
      return <PackagingDataDisplay data={item.data as PackagingRateItemData} ownBoxes={ownBoxes} />;
    default:
      return <RateItemDataFallback data={item.data} />;
  }
};

const ShippingContext = ({ item }: { item: RateItem }) => {
  if (item.type !== 'shipping') return null;

  const fields = extractShippingContext(item);
  if (!hasShippingContext(fields)) return null;

  return <p className="mt-2 text-xs text-subtext">{formatShippingContextSummary(fields)}</p>;
};

export const RateItemsTable = ({ items, ownBoxes = [] }: RateItemsTableProps) => {
  const { t } = useTranslation('ratesModule');

  const displayItems = useMemo(() => partitionRateItems(items), [items]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedCourier, setSelectedCourier] = useState('all');
  const [selectedService, setSelectedService] = useState('all');

  const shippingItems = useMemo(() => displayItems.filter((item) => item.type === 'shipping'), [displayItems]);
  const fulfillmentItems = useMemo(() => displayItems.filter((item) => item.type === 'fulfillment'), [displayItems]);
  const storageItems = useMemo(() => displayItems.filter((item) => item.type === 'storage'), [displayItems]);
  const packagingItems = useMemo(() => displayItems.filter((item) => item.type === 'packaging'), [displayItems]);

  const availableCountries = useMemo(() => {
    const countries = new Set<string>();
    shippingItems.forEach((item) => {
      const country = item.country_name || item.country?.name;
      if (country) countries.add(country);
    });
    return Array.from(countries).sort();
  }, [shippingItems]);

  const availableCouriers = useMemo(() => {
    const couriers = new Set<string>();
    shippingItems.forEach((item) => {
      const courier = item.courier_name || item.courier?.name;
      if (courier) couriers.add(courier);
    });
    return Array.from(couriers).sort();
  }, [shippingItems]);

  const availableServices = useMemo(() => {
    const services = new Set<string>();
    shippingItems.forEach((item) => {
      const service = item.courier_service_name || item.courier_service?.name;
      if (service) services.add(service);
    });
    return Array.from(services).sort();
  }, [shippingItems]);

  const filteredShippingItems = useMemo(() => {
    return shippingItems.filter((item) => {
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const titleMatch = item.title.toLowerCase().includes(searchLower);
        if (!titleMatch) return false;
      }

      if (selectedCountry !== 'all') {
        const country = item.country_name || item.country?.name;
        if (country !== selectedCountry) return false;
      }

      if (selectedCourier !== 'all') {
        const courier = item.courier_name || item.courier?.name;
        if (courier !== selectedCourier) return false;
      }

      if (selectedService !== 'all') {
        const service = item.courier_service_name || item.courier_service?.name;
        if (service !== selectedService) return false;
      }

      return true;
    });
  }, [shippingItems, searchTerm, selectedCountry, selectedCourier, selectedService]);

  const hasActiveFilters = useMemo(() => {
    return (
      searchTerm.trim() !== '' || selectedCountry !== 'all' || selectedCourier !== 'all' || selectedService !== 'all'
    );
  }, [searchTerm, selectedCountry, selectedCourier, selectedService]);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedCountry('all');
    setSelectedCourier('all');
    setSelectedService('all');
  }, []);

  if (displayItems.length === 0) {
    return <div className="text-center py-8 text-subtext">{t('detail.noItems')}</div>;
  }

  const hasShipping = shippingItems.length > 0;
  const hasOtherConcepts = fulfillmentItems.length > 0 || storageItems.length > 0;
  const hasPackaging = packagingItems.length > 0;

  // Determine default tab based on available content
  const defaultTab = hasShipping ? 'shipping' : hasPackaging ? 'packaging' : 'other';

  return (
    <div className="space-y-6">
      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="mb-6">
          {hasShipping && (
            <TabsTrigger value="shipping">
              {t('itemTypes.shipping')} ({filteredShippingItems.length})
            </TabsTrigger>
          )}
          {hasOtherConcepts && (
            <TabsTrigger value="other">
              {t('tabs.otherConcepts')} ({fulfillmentItems.length + storageItems.length})
            </TabsTrigger>
          )}
          {hasPackaging && (
            <TabsTrigger value="packaging">
              {t('tabs.packaging')} ({packagingItems.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Shipping Tab */}
        {hasShipping && (
          <TabsContent value="shipping">
            <ShippingFilters
              search={searchTerm}
              country={selectedCountry}
              courier={selectedCourier}
              service={selectedService}
              onSearchChange={setSearchTerm}
              onCountryChange={setSelectedCountry}
              onCourierChange={setSelectedCourier}
              onServiceChange={setSelectedService}
              onClearFilters={handleClearFilters}
              hasActiveFilters={hasActiveFilters}
              availableCountries={availableCountries}
              availableCouriers={availableCouriers}
              availableServices={availableServices}
            />

            {filteredShippingItems.length === 0 ? (
              <div className="text-center py-8 text-subtext">
                {hasActiveFilters ? t('filters.shipping.noResults') : t('detail.noItems')}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredShippingItems.map((item) => (
                  <div
                    key={item.id}
                    className="border border-card-border rounded-lg p-4 hover:border-primary/50 transition-colors"
                  >
                    <div className="mb-3">
                      <ShippingContext item={item} />
                    </div>

                    <div className="mb-2">
                      <RateItemDataRenderer item={item} ownBoxes={ownBoxes} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* Other Concepts Tab (Fulfillment + Storage) */}
        {hasOtherConcepts && (
          <TabsContent value="other">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Fulfillment - Single item */}
              {fulfillmentItems.length > 0 && (
                <div className="border border-card-border rounded-lg p-5 bg-card-bg/50">
                  <div className="flex items-center gap-2 mb-3">
                    <TypeBadge type="fulfillment" />
                  </div>
                  {fulfillmentItems.map((item) => (
                    <div key={item.id}>
                      <div className="mb-2">
                        <RateItemDataRenderer item={item} ownBoxes={ownBoxes} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Storage - Single item */}
              {storageItems.length > 0 && (
                <div className="border border-card-border rounded-lg p-5 bg-card-bg/50">
                  <div className="flex items-center gap-2 mb-3">
                    <TypeBadge type="storage" />
                  </div>
                  {storageItems.map((item) => (
                    <div key={item.id}>
                      <div className="mb-2">
                        <RateItemDataRenderer item={item} ownBoxes={ownBoxes} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {hasPackaging && (
          <TabsContent value="packaging">
            {packagingItems.map((item) => (
              <RateItemDataRenderer key={item.id} item={item} ownBoxes={ownBoxes} />
            ))}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
